import { useTranslation } from "react-i18next";
import type { SchemaElement } from "../../api/restaurantSchema";
import { textWidthEm } from "./badgeFit";
import { useSchemaFonts } from "./useSchemaFonts";
import { BLOCKS, blockDepthPx, blockShadow } from "./blockDepth";
import { doorGeometry, doorLabelLayout } from "./doorGeometry";
import { ELEMENT_STYLES } from "./elementCatalog";
import { DESIGN_UNIT, objectDimensions } from "./geometry";
import SelectionHandles from "./SelectionHandles";

/**
 * Входът отгоре, както в макета на „Резервации": праг в цвета на входа между
 * две каси, двете крила отворени навътре с дъгите им (както в архитектурен
 * план) и етикетът „Вход" върху прага. Вратата се върти с елемента, а
 * етикетът остава изправен; когато надписът не се събира между касите,
 * остава само иконата — името е в подсказката (`doorLabelLayout`).
 * Размерите са логически единици × `scale` — inline style само за тях
 * (изключение (1)).
 */
const EntranceDoor = ({
  element,
  scale,
  selected,
}: {
  element: SchemaElement;
  scale: number;
  selected: boolean;
}) => {
  useSchemaFonts();
  const { t } = useTranslation();
  const style = ELEMENT_STYLES.entrance;
  const { width, height } = objectDimensions("entrance", element.size);
  const designPixel = DESIGN_UNIT * scale;
  // Рисунката е в пиксели на дизайна — viewBox на SVG-то; × designPixel на екрана.
  const boxWidth = width / DESIGN_UNIT;
  const boxHeight = height / DESIGN_UNIT;
  const door = doorGeometry(boxWidth, boxHeight);

  const label = t(style.labelKey);
  const chip = doorLabelLayout({
    textEm: textWidthEm(label),
    rotation: element.rotation,
    labelRoom: door.labelRoom,
    sillCenter: door.sillCenter,
    designPixel,
  });

  return (
    <div className="relative font-sans" style={{ width: width * scale, height: height * scale }}>
      <div
        className="absolute bottom-0 border-[1.5px] border-schema-entrance-edge bg-schema-entrance"
        style={{
          left: door.jamb * designPixel,
          right: door.jamb * designPixel,
          height: door.jamb * designPixel,
          borderRadius: 2 * designPixel,
        }}
      />
      {(["left-0", "right-0"] as const).map((sideClass) => (
        <div
          key={sideClass}
          className={`absolute bottom-0 bg-schema-wall ${sideClass}`}
          style={{
            width: door.jamb * designPixel,
            height: door.jambHeight * designPixel,
            borderRadius: 2 * designPixel,
            boxShadow: blockShadow({
              rotation: element.rotation,
              depth: blockDepthPx(BLOCKS.wall.depth, designPixel),
              color: BLOCKS.wall.side,
            }),
          }}
        />
      ))}
      <svg
        viewBox={`0 0 ${boxWidth} ${boxHeight}`}
        className="pointer-events-none absolute inset-0 size-full overflow-visible"
        fill="none"
        aria-hidden="true"
      >
        {door.leaves.map(({ hingeX, tipX, closedX, clockwise }) => (
          <g key={hingeX}>
            {/* Дъгата е тънък пунктир при всеки мащаб — не се мащабира с вратата. */}
            <path
              d={`M ${closedX} ${door.sill} A ${door.leaf} ${door.leaf} 0 0 ${clockwise ? 1 : 0} ${tipX} ${door.tipY}`}
              className="stroke-schema-wall/60"
              strokeWidth={1.5}
              strokeDasharray="3 3"
              vectorEffect="non-scaling-stroke"
            />
            <line
              x1={hingeX}
              y1={door.sill}
              x2={tipX}
              y2={door.tipY}
              className="stroke-schema-wall"
              strokeWidth={3.5}
              strokeLinecap="round"
            />
          </g>
        ))}
      </svg>
      <div
        title={chip.showText ? undefined : label}
        className={`absolute left-1/2 flex items-center justify-center gap-[0.4em] whitespace-nowrap border-[1.5px] font-semibold transition-[border-color,box-shadow] duration-150 ${style.bodyClass} ${
          selected ? "!border-brand shadow-[0_0_0_5px_var(--color-schema-halo)]" : ""
        }`}
        style={{
          top: chip.centerY,
          height: chip.height,
          // Само иконата → квадрат.
          width: chip.showText ? undefined : chip.height,
          paddingInline: chip.paddingInline,
          borderRadius: 8 * designPixel,
          fontSize: chip.fontSize,
          transform: `translate(-50%, -50%) rotate(${-element.rotation}deg)`,
        }}
      >
        <style.Icon className="size-[1.05em] shrink-0" />
        {chip.showText && label}
      </div>
      {selected && <SelectionHandles />}
    </div>
  );
};

export default EntranceDoor;
